import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/** Below this the title and description wrap word by word, so the actions move to their own line instead. */
const TITLE_MIN_WIDTH = 320;

interface PageHeaderProps {
  title: React.ReactNode;
  /** One or two sentences under the title. */
  description?: React.ReactNode;
  /** Alias for `description`, kept for existing callers. */
  subtitle?: React.ReactNode;
  /** Right-aligned controls: buttons, a search field, a filter. */
  actions?: React.ReactNode;
  /** Optional back link rendered above the title. */
  backTo?: string;
  backLabel?: string;
  /**
   * `page` is the h1 every signed-in page states once (the top bar shows only
   * the section). `section` is the smaller h2 for a page that renders inside
   * a tab shell which already owns the h1.
   */
  variant?: 'page' | 'section';
  /** Single-action shorthand, kept for existing callers. Prefer `actions`. */
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
}

/**
 * The header row every signed-in page starts with: title, description, and
 * the page-level actions on the right. Pages used to pick their own heading
 * level, weight and colour (h4, h5/600, bold coloured h4, or none at all),
 * so the same row now comes from here.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  subtitle,
  actions,
  backTo,
  backLabel = 'Back',
  variant = 'page',
  actionLabel,
  onAction,
  actionIcon,
}) => {
  const text = description ?? subtitle;
  const legacyAction =
    actionLabel && onAction ? (
      <Button variant="contained" onClick={onAction} startIcon={actionIcon}>
        {actionLabel}
      </Button>
    ) : null;
  const hasActions = Boolean(actions) || Boolean(legacyAction);

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      useFlexGap
      sx={{
        mb: variant === 'page' ? 3 : 2.5,
        columnGap: 2,
        rowGap: 1.5,
        // A wide action row moves to its own line instead of squeezing the title into a narrow column.
        flexWrap: { xs: 'nowrap', md: 'wrap' },
      }}
    >
      <Box sx={{ minWidth: 0, flex: { xs: '0 0 auto', md: `1 1 ${TITLE_MIN_WIDTH}px` }, maxWidth: '100%' }}>
        {backTo && (
          <Button
            component={RouterLink}
            to={backTo}
            size="small"
            startIcon={<ArrowBack />}
            sx={{ ml: -1, mb: 0.5 }}
          >
            {backLabel}
          </Button>
        )}
        {variant === 'page' ? (
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
        ) : (
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
        )}
        {text && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {text}
          </Typography>
        )}
      </Box>
      {hasActions && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            // Shrinks only once it sits on its own line, then wraps its buttons; stays right-aligned at md+.
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            ml: { md: 'auto' },
          }}
        >
          {actions}
          {legacyAction}
        </Stack>
      )}
    </Stack>
  );
};
